// "Payment method on file" section of the client Billing panel (opened from
// the Billing button on My Invoices).
//
// Clients on monthly invoice billing have no visible /my-billing page, so
// this card is their one surface for keeping a chargeable card (or a bank
// account) on Stripe. Cards already saved in Stripe show up here as-is —
// nobody re-enters a card we already have.
// Saving a card here does three things the hosted Stripe portal can't:
//   1. SetupIntent is created with usage=off_session (recurring authorization),
//   2. autopay consent is recorded on the client (auto_charge_invoice refuses
//      to run without it),
//   3. the new card becomes the invoice default.
import React, { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';
import { toast } from '@src/components/core/toaster';
import {
  detachMyPaymentMethod,
  fetchMyPaymentMethods,
  listMyBillingProfiles,
  recordMyAutopayConsent,
  setMyDefaultPaymentMethod,
  createMySetupIntent,
  registerMyPendingBankVerification,
} from '@src/api/myBilling';
import AddBankDialog from '@src/components/billing/AddBankDialog';
import RemovePaymentMethodDialog from '@src/components/billing/RemovePaymentMethodDialog';
import {
  cardExpirationStatus,
  formatCardExpiration,
  formatLastChargeLine,
} from '@src/components/billing/paymentMethodStatus';

function AddCardForm({ clientSecret, consentText, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [consented, setConsented] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !consented) return;
    setSaving(true);
    setError(null);
    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) },
    });
    if (result.error) {
      setError(result.error.message);
      setSaving(false);
      return;
    }
    if (result.setupIntent?.status === 'succeeded') {
      onSuccess(result.setupIntent.payment_method);
    } else {
      setError('Setup did not complete. Please try again.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Your card information is encrypted by Stripe before it leaves your
          browser. We never see your full card number.
        </DialogContentText>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#333' },
                invalid: { color: '#dc2626' },
              },
            }}
          />
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
        <FormControlLabel
          sx={{ mt: 2, alignItems: 'flex-start' }}
          control={
            <Checkbox
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              sx={{ mt: -1 }}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {consentText}
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || saving || !consented}
        >
          {saving ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </form>
  );
}

function AddCardDialog({ open, onClose, profileId, consentText, onAttached }) {
  const [intent, setIntent] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !profileId) return undefined;
    let cancelled = false;
    setIntent(null);
    setLoadError(null);
    createMySetupIntent(profileId, 'card')
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data;
        if (!data?.client_secret || !data?.publishable_key) {
          setLoadError('Unable to start payment setup. Please try again later.');
          return;
        }
        setIntent(data);
        setStripePromise((prev) => prev || loadStripe(data.publishable_key));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.detail || err?.message || 'Failed to start setup.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, profileId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a card for automatic billing</DialogTitle>
      {loadError ? (
        <>
          <DialogContent>
            <Alert severity="error">{loadError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : !intent || !stripePromise ? (
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Securing your connection to Stripe…
            </Typography>
          </Stack>
        </DialogContent>
      ) : (
        <Elements stripe={stripePromise}>
          <AddCardForm
            clientSecret={intent.client_secret}
            consentText={consentText}
            onSuccess={async (newPmId) => {
              // Order matters: consent first (it's what unlocks autopay),
              // then promote the fresh card to invoice default.
              try {
                await recordMyAutopayConsent(profileId, 'card');
              } catch {
                /* non-fatal — staff can re-record */
              }
              if (newPmId) {
                try {
                  await setMyDefaultPaymentMethod(profileId, newPmId);
                } catch {
                  /* non-fatal */
                }
              }
              toast.success(
                'Card saved. Anything past due will be charged to it in the next minute or two.',
              );
              onAttached();
              onClose();
            }}
            onCancel={onClose}
          />
        </Elements>
      )}
    </Dialog>
  );
}

function ProfileMethodsSection({ profile, showClientName }) {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [methodToRemove, setMethodToRemove] = useState(null);
  const [authorizing, setAuthorizing] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchMyPaymentMethods(profile.id)
      .then((resp) => setPayment(resp?.data || null))
      .catch(() => setPayment(null))
      .finally(() => setLoading(false));
  }, [profile.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const methods = payment?.methods || [];
  const cards = methods.filter((m) => m.type === 'card');
  const usableCards = cards.filter((m) => cardExpirationStatus(m) !== 'expired');
  const consent = payment?.autopay_consent || { authorized: false, methods: [] };
  const cardConsent = (consent.methods || []).includes('card');
  const consentText =
    payment?.autopay_consent_text ||
    'I authorize Lead Gurus to securely store this payment method and automatically charge it for future invoices for this billing account.';
  const ready = usableCards.length > 0 && cardConsent;
  const health = payment?.health || null;
  const declined = health?.status === 'failing';

  const handleAuthorizeExisting = async () => {
    setAuthorizing(true);
    try {
      await recordMyAutopayConsent(profile.id, 'card');
      toast.success('Automatic billing authorized.');
      refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || 'Could not record authorization.',
      );
    } finally {
      setAuthorizing(false);
    }
  };

  return (
    <Box>
      {showClientName && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {profile.client_name || profile.owner_name}
        </Typography>
      )}
      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading…
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {declined && (
            <Alert severity="error">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Your card was declined
              </Typography>
              <Typography variant="body2">
                {health.method_label ? `${health.method_label}: ` : ''}
                {health.last_failure_message || 'the bank refused the last charge.'} Add a new
                card below — anything past due is charged to it right away and your account
                stays active.
              </Typography>
            </Alert>
          )}
          {!declined && !usableCards.length && (
            <Alert severity="warning">
              {cards.length
                ? 'Your card on file has expired — please add a new card so invoices can be billed automatically.'
                : 'No card on file yet. Add a card to authorize automatic billing of your monthly invoices.'}
            </Alert>
          )}
          {usableCards.length > 0 && !cardConsent && (
            <Alert
              severity="warning"
              action={
                <Button
                  size="small"
                  color="inherit"
                  variant="outlined"
                  onClick={handleAuthorizeExisting}
                  disabled={authorizing}
                >
                  {authorizing ? 'Saving…' : 'Authorize'}
                </Button>
              }
            >
              Your card is saved, but automatic billing isn&apos;t authorized
              yet. Authorizing lets us charge invoices to your card when
              they&apos;re due — no manual payment needed.
            </Alert>
          )}
          {ready && !declined && (
            <Alert severity="success">
              You&apos;re set up for automatic billing — invoices are charged
              to your card when due.
            </Alert>
          )}
          {methods.map((m) => {
            const expStatus = cardExpirationStatus(m);
            const expLabel = formatCardExpiration(m);
            const lastChargeLine = formatLastChargeLine(m);
            const lastFailed = m.last_charge && m.last_charge.succeeded === false;
            return (
              <Stack
                key={m.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack spacing={0.25}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">{m.label}</Typography>
                    <Chip
                      size="small"
                      label={m.type === 'card' ? 'Card' : 'Bank'}
                      color={m.type === 'card' ? 'primary' : 'success'}
                      variant="outlined"
                    />
                    {m.is_default && (
                      <Chip size="small" label="Primary" color="success" />
                    )}
                    {expStatus === 'expired' && (
                      <Chip size="small" label="Expired" color="error" />
                    )}
                    {expStatus === 'expiring' && (
                      <Chip size="small" label="Expires soon" color="warning" />
                    )}
                    {lastFailed && (
                      <Chip
                        size="small"
                        label="Last charge declined"
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  {(expLabel || lastChargeLine) && (
                    <Typography variant="caption" color="text.secondary">
                      {expLabel}
                      {expLabel && lastChargeLine ? ' • ' : ''}
                      {lastChargeLine}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {!m.is_default && expStatus !== 'expired' && (
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          await setMyDefaultPaymentMethod(profile.id, m.id);
                          toast.success('Primary card updated.');
                          refresh();
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail ||
                              err?.message ||
                              'Could not update primary.',
                          );
                        }
                      }}
                    >
                      Make primary
                    </Button>
                  )}
                  {!m.is_default && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setMethodToRemove(m)}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              </Stack>
            );
          })}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant={usableCards.length && !declined ? 'outlined' : 'contained'}
              startIcon={<AddCardIcon />}
              onClick={() => setAddCardOpen(true)}
            >
              {usableCards.length ? 'Add / replace card' : 'Add card'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AccountBalanceIcon />}
              onClick={() => setAddBankOpen(true)}
            >
              Connect bank (ACH)
            </Button>
          </Stack>
        </Stack>
      )}

      <AddCardDialog
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        profileId={profile.id}
        consentText={consentText}
        onAttached={refresh}
      />

      <AddBankDialog
        open={addBankOpen}
        onClose={() => setAddBankOpen(false)}
        profileId={profile.id}
        createSetupIntent={createMySetupIntent}
        registerPendingVerification={registerMyPendingBankVerification}
        onAttached={async () => {
          try {
            await recordMyAutopayConsent(profile.id, 'us_bank_account');
          } catch {
            /* non-fatal — staff can re-record */
          }
          refresh();
        }}
        settlementCopy={
          'Bank payments take 3–5 business days to clear and carry no card fee. ' +
          'Keep a card on file too — your agreement uses it as the backup.'
        }
      />

      <RemovePaymentMethodDialog
        open={Boolean(methodToRemove)}
        method={methodToRemove}
        onClose={() => setMethodToRemove(null)}
        onConfirm={async (m) => {
          await detachMyPaymentMethod(profile.id, m.id);
          toast.success('Payment method removed.');
          setMethodToRemove(null);
          refresh();
        }}
      />
    </Box>
  );
}

export default function PaymentMethodOnFileCard({ profiles: profilesProp = null }) {
  // Staff on their own see nothing (the unscoped endpoint would return every
  // profile); staff previewing a client see exactly what the client sees.
  const effectiveUser = useEffectiveUser();
  const isStaff = Boolean(effectiveUser?.is_staff || effectiveUser?.is_superuser);
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (isStaff || profilesProp) return;
    listMyBillingProfiles()
      .then((resp) => {
        const data = resp?.data;
        const rows = Array.isArray(data) ? data : data?.results || [];
        setFetched(rows);
      })
      .catch(() => setFetched([]));
  }, [isStaff, profilesProp]);

  const profiles = profilesProp || fetched;
  if (isStaff || !profiles?.length) return null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardHeader
        title="Payment method on file"
        subheader="Your agreement keeps a working card on file. Saved cards and banks are charged automatically when an invoice is due."
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Stack spacing={3}>
          {profiles.map((p) => (
            <ProfileMethodsSection
              key={p.id}
              profile={p}
              showClientName={profiles.length > 1}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
